import { Link } from "react-router-dom";
import { FileText, ShoppingBag, Palette, Copyright, Scale, Mail } from "lucide-react";

export default function Terms() {
    return (
        <div className="relative z-10 pt-24 sm:pt-32 pb-24" data-testid="terms-page">
            <div className="max-w-[900px] mx-auto px-6 sm:px-10">
                <div className="mb-16 fade-up">
                    <div className="label-caps text-[#8C3A3A] mb-4">Studio Policy</div>
                    <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter text-[#1A1A1A] leading-none mb-8">
                        Terms<br /><span className="italic text-[#5A5A5A]">of service.</span>
                    </h1>
                    <p className="text-[#5A5A5A] leading-relaxed max-w-2xl">
                        The plain-language agreement between you and Art N Moore. By using this site or placing an
                        order, you agree to the terms below.
                    </p>
                </div>

                <div className="space-y-16">
                    {/* Introduction */}
                    <section data-testid="terms-intro">
                        <div className="flex items-center gap-3 mb-6">
                            <FileText className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">1. About These Terms</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                "Art N Moore", "we", "us", and "our" refer to the solo art practice of Nicholas Moore,
                                based in Australia. "You" refers to any visitor, browser, or customer of the site
                                <strong className="text-[#1A1A1A]"> artnmoore.com.au</strong>.
                            </p>
                            <p>
                                These terms govern your use of the site and any purchase you make. They should be read
                                alongside our{" "}
                                <Link to="/privacy" className="text-[#1A1A1A] underline hover:text-[#8C3A3A]">Privacy Policy</Link>{" "}
                                and{" "}
                                <Link to="/policies" className="text-[#1A1A1A] underline hover:text-[#8C3A3A]">Shipping & Returns Policy</Link>.
                            </p>
                        </div>
                    </section>

                    {/* Products */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="terms-products">
                        <div className="flex items-center gap-3 mb-6">
                            <Palette className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">2. What We Sell</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <ul className="list-disc pl-6 space-y-2">
                                <li><strong className="text-[#1A1A1A]">Fine art prints</strong> — printed to order on
                                    archival paper. Editions are open unless specifically stated as limited.</li>
                                <li><strong className="text-[#1A1A1A]">Original works</strong> — one-of-a-kind
                                    charcoal, coloured pencil, and acrylic pieces. Once sold, they are permanently
                                    removed from the live catalogue.</li>
                                <li><strong className="text-[#1A1A1A]">Commissioned works</strong> — bespoke
                                    portraits, subject to a separate written commission agreement.</li>
                            </ul>
                            <p>
                                We make reasonable efforts to display colours and finishes accurately, but slight
                                variation between your screen and the physical print is normal and does not constitute
                                a fault.
                            </p>
                        </div>
                    </section>

                    {/* Orders & Payment */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="terms-orders">
                        <div className="flex items-center gap-3 mb-6">
                            <ShoppingBag className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">3. Orders, Pricing & Payment</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                Placing an order is an offer to purchase, which we accept when we send you a confirmation
                                email. We reserve the right to decline or cancel any order — for example, if a piece has
                                already sold, a pricing error is detected, or fraud is suspected. If we cancel an accepted
                                order, you will receive a full refund.
                            </p>
                            <p>
                                All prices are listed in Australian Dollars (AUD) unless otherwise stated. Prices exclude
                                any applicable import duties, taxes, or customs fees for international orders — those
                                remain your responsibility.
                            </p>
                            <p>
                                Payment is processed securely by <strong className="text-[#1A1A1A]">Stripe</strong>. We do
                                not store your card details. Full payment is required before any print or original is
                                dispatched.
                            </p>
                        </div>
                    </section>

                    {/* Shipping & returns */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="terms-shipping">
                        <div className="flex items-center gap-3 mb-6">
                            <FileText className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">4. Shipping & Returns</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                Full shipping timeframes, carriers, and our returns policy — including damage claims and
                                refund windows — are set out in the{" "}
                                <Link to="/policies" className="text-[#1A1A1A] underline hover:text-[#8C3A3A]">Shipping & Returns Policy</Link>,
                                which forms part of these terms.
                            </p>
                            <p>
                                Nothing in these terms limits your rights as a consumer under the{" "}
                                <strong className="text-[#1A1A1A]">Australian Consumer Law</strong>.
                            </p>
                        </div>
                    </section>

                    {/* IP */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="terms-ip">
                        <div className="flex items-center gap-3 mb-6">
                            <Copyright className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">5. Copyright & Intellectual Property</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                All artworks, prints, photographs, text, and design elements on this site are the
                                intellectual property of <strong className="text-[#1A1A1A]">Nicholas Moore</strong> and
                                are protected by copyright law.
                            </p>
                            <p>
                                When you buy a print or an original, you are purchasing the physical object. You do{" "}
                                <strong className="text-[#1A1A1A]">not</strong> acquire the right to:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>Reproduce, copy, or resell prints of the artwork.</li>
                                <li>Use the image commercially — including on merchandise, advertising, NFTs, or
                                    editorial reproductions — without prior written permission.</li>
                                <li>Modify, adapt, or create derivative works.</li>
                            </ul>
                            <p>
                                You are welcome to enjoy the work in your home, gift it, or resell the physical
                                original object at your discretion. For any licensing enquiry, contact the studio.
                            </p>
                        </div>
                    </section>

                    {/* Commissions */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="terms-commissions">
                        <div className="flex items-center gap-3 mb-6">
                            <Palette className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">6. Commissions</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                Commissioned works are governed by a separate written agreement covering deposit,
                                revision rounds, delivery, and copyright. Standard terms:
                            </p>
                            <ul className="list-disc pl-6 space-y-2">
                                <li>A <strong className="text-[#1A1A1A]">50% deposit</strong> is required before work
                                    begins. The deposit is non-refundable once the artist has commenced the piece.</li>
                                <li><strong className="text-[#1A1A1A]">Two rounds of revision</strong> are included at
                                    agreed checkpoints.</li>
                                <li>The final balance is due before the finished work is shipped.</li>
                                <li>Copyright in the completed commission remains with the artist; the client owns the
                                    physical work and receives personal display rights.</li>
                            </ul>
                            <p>
                                The artist reserves the right to decline any commission at their discretion.
                            </p>
                        </div>
                    </section>

                    {/* Content note */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="terms-content">
                        <div className="flex items-center gap-3 mb-6">
                            <Scale className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">7. Content & Age</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                Some works on this site depict artistic nudity and sensual portraiture of the male
                                form. Explicit works are blurred by default and revealed only by deliberate click.
                            </p>
                            <p>
                                By revealing or purchasing such work, you confirm you are of legal age in your
                                jurisdiction (18+ in Australia) and are viewing this content voluntarily. The artwork
                                is offered as fine art and is not intended for use in advertising or commercial
                                pornographic contexts.
                            </p>
                        </div>
                    </section>

                    {/* Liability */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="terms-liability">
                        <div className="flex items-center gap-3 mb-6">
                            <Scale className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">8. Limitation of Liability</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                To the maximum extent permitted by law, Art N Moore is not liable for any indirect,
                                incidental, or consequential loss arising from your use of the site or from a
                                purchase. Nothing in this section excludes any liability that cannot be excluded under
                                the Australian Consumer Law or other applicable law.
                            </p>
                            <p>
                                Our total liability for any single order is limited to the amount you paid for that
                                order.
                            </p>
                        </div>
                    </section>

                    {/* Governing law */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="terms-law">
                        <div className="flex items-center gap-3 mb-6">
                            <Scale className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">9. Governing Law & Changes</h2>
                        </div>
                        <div className="prose text-[#5A5A5A] leading-relaxed space-y-4 text-base">
                            <p>
                                These terms are governed by the laws of Australia. Any dispute arising from them will
                                be subject to the exclusive jurisdiction of the Australian courts.
                            </p>
                            <p>
                                We may update these terms from time to time. The version in force is always the one
                                published on this page. If you have an open order or commission when we update these
                                terms, your order is governed by the version that was live when you placed it.
                            </p>
                        </div>
                    </section>

                    {/* Contact */}
                    <section className="border-t border-black/[0.06] pt-16" data-testid="terms-contact">
                        <div className="flex items-center gap-3 mb-6">
                            <Mail className="w-4 h-4 text-[#8C3A3A]" />
                            <h2 className="label-caps text-[#1A1A1A]">10. Contact</h2>
                        </div>
                        <p className="text-[#5A5A5A] leading-relaxed text-base mb-6">
                            Any questions about these terms, licensing enquiries, or commission agreements can be
                            addressed directly to the studio.
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <a
                                href="mailto:art.nickmoore@gmail.com"
                                className="inline-flex items-center gap-3 bg-[#1A1A1A] text-[#FAFAF7] px-8 py-4 rounded-full text-sm tracking-wide font-medium hover:bg-[#8C3A3A] transition-colors"
                                data-testid="terms-email-btn"
                            >
                                <Mail className="w-4 h-4" /> art.nickmoore@gmail.com
                            </a>
                            <Link
                                to="/policies"
                                className="inline-flex items-center gap-3 border border-black/20 text-[#1A1A1A] px-8 py-4 rounded-full text-sm tracking-wide hover:bg-black/5 transition-colors"
                                data-testid="terms-shipping-link"
                            >
                                Shipping & Returns
                            </Link>
                            <Link
                                to="/privacy"
                                className="inline-flex items-center gap-3 border border-black/20 text-[#1A1A1A] px-8 py-4 rounded-full text-sm tracking-wide hover:bg-black/5 transition-colors"
                                data-testid="terms-privacy-link"
                            >
                                Privacy Policy
                            </Link>
                        </div>
                    </section>
                </div>

                <div className="mt-24 pt-8 border-t border-black/[0.06] text-xs text-[#8A8A8A] tracking-widest uppercase">
                    Last updated {new Date().toLocaleDateString("en-AU", { month: "long", year: "numeric" })}
                </div>
            </div>
        </div>
    );
}
